import React, { useState, useMemo, useCallback } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar.tsx';
import Footer from '../components/Footer.tsx';
import { ResultGroup } from '../components/ResultGroup.tsx';
import { CollapsibleResultGroup } from '../components/CollapsibleResultGroup.tsx';
import FileTreeNavigation from '../components/FileTreeNavigation.tsx';
import EnhancedFileTreeNavigation from '../components/EnhancedFileTreeNavigation.tsx';
import CodePanel from '../components/CodePanel.tsx';
import { CodeDrawer } from '../components/CodeDrawer.tsx';
import { ScanSkeleton } from '../components/ui/CategorySkeleton.tsx';
import { ProgressBar } from '../components/ui/ProgressBar.tsx';
import { SeverityFilter, type SeverityLevel } from '../components/ui/SeverityFilter.tsx';
import FeedbackFAB from '../components/ui/FeedbackFAB.tsx';
import FeedbackModal from '../components/ui/FeedbackModal.tsx';
import { ScanFinding, GroupedFindings, CategoryScores } from '../types/scan.ts';
import { 
  filterGroupedFindingsBySeverity, 
  getTotalFindingsCount,
  getSeverityBreakdown 
} from '../utils/resultFilters.ts';
import { Loader2, Shield, Eye, FileCheck, Activity, Clock, HardDrive, Github, Upload, Info, Lock, CheckCircle, Filter, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSub } from '../contexts/SubscriptionContext.tsx';
import { useScanResults } from '../hooks/useScanResults.ts';
import { useFeedbackModal } from '../hooks/useFeedbackModal.ts';
import { DuplicateToggle } from '../components/ui/DuplicateToggle.tsx';
import { deduplicateFindings, expandGroupedFindings, getDeduplicationStats } from '../utils/findingUtils.ts';
import { getScannerDisplayName, getScannerDescription } from '../utils/scannerDisplayNames.ts';
import AIFixAssistant from '../components/AIFixAssistant.tsx';

// Category metadata with icons and descriptions
const CATEGORY_METADATA = {
  security: {
    label: 'Security',
    icon: Shield,
    description: 'Vulnerabilities, secrets, and security misconfigurations',
    color: 'red',
    maxPossible: 6
  },
  privacy: {
    label: 'Privacy',
    icon: Eye,
    description: 'PII exposure, logging issues, and privacy compliance',
    color: 'blue',
    maxPossible: 2
  },
  compliance: {
    label: 'Compliance',
    icon: FileCheck,
    description: 'RLS, RBAC, headers, and legal requirements',
    color: 'green',
    maxPossible: 3
  },
  miscellaneous: {
    label: 'Miscellaneous',
    icon: Activity,
    description: 'Other issues and general recommendations',
    color: 'gray',
    maxPossible: 1
  }
} as const;

// Issues Score Card Component
const IssuesScoreCard: React.FC<{
  title: string;
  totalIssues: number;
  maxPossible: number;
  icon: React.ComponentType<any>;
  tooltip: string;
  locked?: boolean;
}> = ({ title, totalIssues, maxPossible, icon: Icon, tooltip, locked = false }) => {
  const scorePercent = locked ? 0 : Math.max(0, Math.round(100 - (totalIssues / maxPossible) * 100));
  
  const getScoreColor = (score: number) => {
    if (locked) return 'text-gray-500';
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBackground = (score: number) => {
    if (locked) return 'bg-gray-900/20 border-gray-700/50';
    if (score >= 80) return 'bg-green-900/20 border-green-700/50';
    if (score >= 60) return 'bg-yellow-900/20 border-yellow-700/50';
    return 'bg-red-900/20 border-red-700/50';
  };

  if (!locked && totalIssues === 0) {
    return (
      <div className="p-4 rounded-lg border bg-green-900/20 border-green-700/50 group relative">
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-5 h-5 text-green-400" />
          <span className="text-sm font-medium text-green-300">{title}</span>
          <Info className="w-4 h-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-green-400" />
        </div>
        <div className="text-center mt-2">
          <div className="text-lg font-bold text-green-300">All Clear!</div>
        </div>
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
          {tooltip}
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-lg border ${getScoreBackground(scorePercent)} group relative`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5 text-gray-400" />
        <span className="text-sm font-medium text-gray-300">{title}</span>
        {locked && <Lock className="w-4 h-4 text-gray-500" />}
        <Info className="w-4 h-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className={`text-2xl font-bold ${getScoreColor(scorePercent)} mb-1`}>
        {locked ? '--' : `${scorePercent}%`}
      </div>
      <div className="text-xs text-gray-400">
        {locked ? 'Locked' : `${totalIssues} of ${maxPossible} potential issue${maxPossible !== 1 ? 's' : ''} found`}
      </div>
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
        {tooltip}
      </div>
    </div>
  );
};

// Category Section Component
const CategorySection: React.FC<{
  categoryKey: keyof GroupedFindings;
  findings: ScanFinding[];
  isProUser: boolean;
  isDemoScan: boolean;
  onJumpToFinding?: (finding: ScanFinding) => void;
}> = ({ categoryKey, findings, isProUser, isDemoScan, onJumpToFinding }) => {
  const category = CATEGORY_METADATA[categoryKey];
  const Icon = category.icon;
  
  const hasPremiumFindings = findings.some(f => f.is_premium);
  const hasNonPremiumFindings = findings.some(f => !f.is_premium);
  const isLocked = !isProUser && hasPremiumFindings && !hasNonPremiumFindings && !isDemoScan;
  const visibleFindings = isProUser || isDemoScan ? findings : findings.filter(f => !f.is_premium);
  const issueCount = visibleFindings.length;

  return (
    <div className="mb-8 relative">
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg bg-${category.color}-900/20 border border-${category.color}-700/50`}>
          <Icon className={`w-6 h-6 text-${category.color}-400`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-white">{category.label}</h2>
            {isLocked && <Lock className="w-5 h-5 text-gray-500" />}
          </div>
          <p className="text-sm text-gray-400">{category.description}</p>
        </div>
        <div className="text-right">
          {isLocked ? (
            <span className="text-sm text-gray-500">(locked)</span>
          ) : issueCount === 0 ? (
            <span className="text-sm text-green-400">(no issues)</span>
          ) : (
            <span className="text-sm text-gray-300">
              ({issueCount} issue{issueCount !== 1 ? 's' : ''})
            </span>
          )}
        </div>
      </div>

      {isLocked ? (
        <div className="p-6 rounded-lg border border-gray-700/50 bg-gray-900/20 text-center mb-4">
          <Lock className="w-12 h-12 text-gray-500 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">Premium Feature</h3>
          <p className="text-gray-400 mb-4">
            Upgrade to Pro to unlock {category.label.toLowerCase()} analysis with detailed recommendations.
          </p>
        </div>
      ) : issueCount === 0 ? (
        <div className="p-6 rounded-lg border border-green-700/50 bg-green-900/20 text-center mb-4">
          <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-lg font-medium text-green-300 mb-2">All Clear!</h3>
          <p className="text-green-400">
            ✅ No {category.label.toLowerCase()} issues found
          </p>
        </div>
      ) : (
        <div className="space-y-4 mb-4">
          {Object.entries(
            visibleFindings.reduce((acc, finding) => {
              if (!acc[finding.module_id]) {
                acc[finding.module_id] = [];
              }
              acc[finding.module_id].push(finding);
              return acc;
            }, {} as Record<string, ScanFinding[]>)
          ).map(([scannerId, scannerFindings]) => {
            return (
              <div key={scannerId} className="relative z-10">
              <CollapsibleResultGroup
                findings={scannerFindings}
                title={getScannerDisplayName(scannerId)}
                description={getScannerDescription(scannerId)}
                isProUser={isProUser}
                defaultExpanded={false}
                onJumpToFinding={onJumpToFinding}
              />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function ScanResultsScreen() {
  const { scanId } = useParams<{ scanId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // AI Fix Assistant state management
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(() => {
    // Open by default unless user has closed in this session
    return sessionStorage.getItem('aiFixesPanelClosed') !== 'true';
  });
  const [aiAssistantPhase, setAIAssistantPhase] = useState<'initial' | 'loading' | 'generated' | 'error'>('initial');
  
  // Get subscription data
  const { data: subscription, canScan } = useSub();
  const isProUser = !!(subscription && subscription.plan === 'pro');
  
  // Get scan results using the enhanced hook
  const {
    groupedFindings,
    scanData,
    loading: isLoading,
    error,
    progress,
  } = useScanResults('real', scanId || '');

  // Feedback modal state
  const { isOpen, openModal, closeModal, initialPage } = useFeedbackModal();

  // File selection and filtering
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [activeFinding, setActiveFinding] = useState<ScanFinding | null>(null); // For jump-to-finding functionality
  const [hideDuplicates, setHideDuplicates] = useState(true); // Default to hiding duplicates
  const [isCodePanelExpanded, setIsCodePanelExpanded] = useState(false); // For expanding code panel

  // Get severity filters from URL
  const selectedSeverities: SeverityLevel[] = useMemo(() => {
    const filters = searchParams.get('severity');
    if (filters) {
      return filters.split(',').filter(f => 
        ['critical', 'high', 'medium', 'low', 'info'].includes(f)
      ) as SeverityLevel[];
    }
    return ['critical', 'high', 'medium', 'low', 'info'];
  }, [searchParams]);

  // Filter findings based on severity selection
  const filteredFindings = useMemo(() => {
    if (!groupedFindings) return { security: [], privacy: [], compliance: [], miscellaneous: [] };
    return filterGroupedFindingsBySeverity(groupedFindings, selectedSeverities);
  }, [groupedFindings, selectedSeverities]);

  // Apply deduplication to each category
  const deduplicatedFindings = useMemo(() => {
    const result = { security: [], privacy: [], compliance: [], miscellaneous: [] } as any;
    
    Object.entries(filteredFindings).forEach(([category, findings]) => {
      const groupedCategory = deduplicateFindings(findings);
      result[category] = expandGroupedFindings(groupedCategory, hideDuplicates);
    });
    
    return result;
  }, [filteredFindings, hideDuplicates]);

  // Get deduplication statistics for all findings
  const deduplicationStats = useMemo(() => {
    if (!groupedFindings) return { totalFindings: 0, uniqueFindings: 0, duplicateGroups: 0, duplicatesRemoved: 0, duplicatePercentage: 0 };
    const allFindings = Object.values(groupedFindings).flat();
    return getDeduplicationStats(allFindings);
  }, [groupedFindings]);

  const totalFindings = groupedFindings ? getTotalFindingsCount(groupedFindings) : 0;
  const filteredTotalFindings = getTotalFindingsCount(filteredFindings);
  const deduplicatedTotalFindings = getTotalFindingsCount(deduplicatedFindings);

  // Get all findings for file tree
  const allFindings = useMemo(() => {
    if (!groupedFindings) return [];
    return Object.values(groupedFindings).flat();
  }, [groupedFindings]);

  // Get actual files from the scan - only show files that were actually scanned
  const getAllFiles = useMemo(() => {
    const extractedFiles = scanData?.metadata?.extracted_files;
    
    // If we have extracted files metadata, use it (this is the proper way)
    if (extractedFiles && extractedFiles.length > 0) {
      return extractedFiles;
    }
    
    // Fallback: only show files that have findings (no fake common files)
    // This ensures we only show files that were actually in the uploaded ZIP/repo
    const findingFiles = new Set(allFindings.map(f => f.file_path));
    
    // Return only the actual finding files, sorted
    return Array.from(findingFiles).sort();
  }, [scanData?.metadata?.extracted_files, allFindings]);

  // Get findings for selected file
  const selectedFileFindings = useMemo(() => {
    if (!selectedFile || !allFindings.length) return [];
    return allFindings.filter(finding => finding.file_path === selectedFile);
  }, [selectedFile, allFindings]);

  // Handle file selection from FileTreeNavigation
  const handleFileSelect = useCallback((filePath: string | null) => {
    console.log('[ScanResultsScreen] File selected:', filePath);
    setSelectedFile(filePath);
    setActiveFinding(null); // Clear active finding when selecting a different file
  }, []);

  // Handle jumping to a specific finding in the code
  const handleJumpToFinding = useCallback((finding: ScanFinding) => {
    console.log('[ScanResultsScreen] Jumping to finding:', finding.file_path, 'line', finding.line);
    setSelectedFile(finding.file_path);
    setActiveFinding(finding);
  }, []);

  // Handle CodePanel close
  const handleCodePanelClose = () => {
    console.log('[ScanResultsScreen] Closing code panel');
    setSelectedFile(null);
    setActiveFinding(null);
  };

  // Handle code panel expand/collapse
  const handleCodePanelExpand = () => {
    console.log('[ScanResultsScreen] Toggling code panel expand:', !isCodePanelExpanded);
    setIsCodePanelExpanded(!isCodePanelExpanded);
  };

  const handleFiltersChange = (filters: SeverityLevel[]) => {
    console.log('[ScanResultsScreen] Severity filters changed:', filters);
  };

  // Handle AI Assistant toggle
  const handleAIAssistantToggle = useCallback(() => {
    setIsAIAssistantOpen(prev => !prev);
    if (!isAIAssistantOpen) {
      setAIAssistantPhase('initial');
    }
  }, [isAIAssistantOpen]);

  const handleAIAssistantClose = useCallback(() => {
    setIsAIAssistantOpen(false);
    setAIAssistantPhase('initial');
  }, []);

  console.log('[ScanResultsScreen] Debug State:', {
    scanId,
    isLoading,
    error,
    hasScanData: !!scanData,
    hasGroupedFindings: !!groupedFindings,
    scanDataFindingsCount: scanData?.findings?.length || 0,
    groupedFindingsBreakdown: groupedFindings ? {
      security: groupedFindings.security?.length || 0,
      privacy: groupedFindings.privacy?.length || 0,
      compliance: groupedFindings.compliance?.length || 0,
      miscellaneous: groupedFindings.miscellaneous?.length || 0
    } : null,
    totalFindings,
    filteredTotalFindings,
    progressStage: progress?.stage,
    isAIAssistantOpen,
    aiAssistantPhase
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <Navbar />
        <main className="container mx-auto px-4 py-8">
          {progress && <ProgressBar progress={progress} />}
          <ScanSkeleton />
        </main>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <Navbar />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-red-400 mb-2">Error Loading Results</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <div className="text-xs text-gray-500">
              Scan ID: {scanId}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar />
      
      {/* Main Layout Container with ARIA landmarks - Responsive Layout */}
      <main className="flex h-[calc(100vh-64px)] overflow-hidden" role="main">
        {/* File Explorer Navigation - Left Column - ARIA landmark */}
        {scanData && (
          <nav 
            aria-label="File Explorer" 
            className={`flex-shrink-0 border-r border-gray-700 transition-all duration-300 ${
              isAIAssistantOpen ? 'w-64' : 'w-80'
            }`}
          >
            <EnhancedFileTreeNavigation
              findings={allFindings}
              allFiles={getAllFiles}
              selectedFile={selectedFile}
              onFileSelect={handleFileSelect}
              isProUser={isProUser}
              className="h-full"
            />
          </nav>
        )}
        
        {/* Code Viewer - Middle-Left Column - ARIA landmark */}
        {selectedFile && (
          <aside 
            aria-label="Code Viewer" 
            className="flex-shrink-0"
          >
            <CodePanel
              fileName={selectedFile}
              findings={selectedFileFindings}
              isProUser={isProUser}
              onClose={handleCodePanelClose}
              onExpand={handleCodePanelExpand}
              isExpanded={isCodePanelExpanded}
              className="h-full"
              activeFinding={activeFinding || undefined}
              scanId={scanId ?? null}
            />
          </aside>
        )}
        
        {/* Scan Results - Center Column - ARIA landmark */}
        <section 
          aria-label="Scan Results" 
          className="flex-1 min-w-0 bg-gray-900 overflow-hidden relative"
        >
          <div className="h-full overflow-y-auto">
            <div className="px-6 py-8 pb-32">
              <div className={`mx-auto transition-all duration-300 ${
                // Dynamic max width based on available space and AI assistant state
                isAIAssistantOpen
                  ? selectedFile && isCodePanelExpanded 
                    ? 'max-w-3xl' // Smallest when both code panel expanded and AI assistant open
                    : selectedFile 
                      ? 'max-w-4xl' // Small when code panel normal and AI assistant open
                      : 'max-w-5xl' // Medium when no code panel but AI assistant open
                  : selectedFile && isCodePanelExpanded 
                    ? 'max-w-4xl' // Normal when code panel expanded, no AI assistant
                    : selectedFile 
                      ? 'max-w-5xl' // Normal when code panel normal, no AI assistant
                      : 'max-w-6xl' // Largest when no code panel and no AI assistant
              }`}>
                <div>
                {/* Header with scan metadata */}
                {scanData && (
                  <header className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          {scanData?.source_type === 'github' ? (
                            <Github className="w-5 h-5 text-blue-400" />
                          ) : scanData?.source_type === 'upload' ? (
                            <Upload className="w-5 h-5 text-green-400" />
                          ) : (
                            <Activity className="w-5 h-5 text-purple-400" />
                          )}
                          <span className="text-sm font-medium text-gray-300">
                            {scanData?.source_type === 'github' ? 'GitHub Repository' : 
                             scanData?.source_type === 'upload' ? 'ZIP Upload' : 
                             'Scan Results'}
                          </span>
                        </div>
                        {scanData?.metadata?.file_size && (
                          <div className="flex items-center gap-2 text-sm text-gray-400">
                            <HardDrive className="w-4 h-4" />
                            {scanData.metadata.file_size > 1024 * 1024 
                              ? `${(scanData.metadata.file_size / (1024 * 1024)).toFixed(1)} MB`
                              : `${(scanData.metadata.file_size / 1024).toFixed(1)} KB`}
                          </div>
                        )}
                      </div>
                      
                      {/* AI Fix Assistant Toggle Button */}
                      {allFindings.length > 0 && (
                        <button
                          onClick={handleAIAssistantToggle}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            isAIAssistantOpen
                              ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                              : 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'
                          }`}
                          title={isAIAssistantOpen ? 'Close AI Fix Assistant' : 'Open AI Fix Assistant'}
                        >
                          <Sparkles className={`w-4 h-4 ${isAIAssistantOpen ? 'animate-pulse' : ''}`} />
                          <span>AI Fixes</span>
                          {isAIAssistantOpen ? (
                            <ChevronRight className="w-4 h-4" />
                          ) : (
                            <ChevronLeft className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </header>
                )}

                {/* Results Header */}
                  <header className="mb-8">
                  <div className="flex items-center gap-4 mb-4">
                    <h1 className="text-3xl font-bold">Scan Results</h1>
                    <div className="text-sm text-gray-400">
                      {hideDuplicates && deduplicationStats.duplicatesRemoved > 0
                        ? `${deduplicatedTotalFindings} unique issue${deduplicatedTotalFindings !== 1 ? 's' : ''} (${deduplicationStats.duplicatesRemoved} duplicates hidden)`
                        : deduplicatedTotalFindings === totalFindings 
                          ? `${totalFindings} issue${totalFindings !== 1 ? 's' : ''} found`
                          : `${deduplicatedTotalFindings} of ${totalFindings} issue${totalFindings !== 1 ? 's' : ''} shown`
                      }
                    </div>
                  </div>
                  
                  {selectedFile && (
                      <div className="text-gray-400 mb-4">
                      Showing results for: <span className="text-blue-400 font-mono">{selectedFile}</span>
                    </div>
                  )}

                  {/* Filters */}
                    <div className="flex flex-wrap items-center gap-4 mb-6">
                    <SeverityFilter onFiltersChange={handleFiltersChange} isSticky={true} />
                    <DuplicateToggle
                      hideDuplicates={hideDuplicates}
                      onToggle={setHideDuplicates}
                      duplicateCount={deduplicationStats.duplicatesRemoved}
                      totalCount={totalFindings}
                    />
                  </div>
                </header>

                {/* Results Categories */}
                  <div className="space-y-12 pb-8">
                  {Object.entries(deduplicatedFindings).map(([categoryKey, findings]) => {
                    if ((findings as ScanFinding[]).length === 0) return null;
                    
                    return (
                      <CategorySection
                        key={categoryKey}
                        categoryKey={categoryKey as keyof GroupedFindings}
                        findings={findings as ScanFinding[]}
                        isProUser={isProUser}
                        isDemoScan={false}
                        onJumpToFinding={handleJumpToFinding}
                      />
                    );
                  })}
                    
                    {/* Show empty state within results interface if no findings */}
                    {totalFindings === 0 && (
                      <div className="text-center py-12">
                        <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-green-400 mb-2">All Clear!</h2>
                        <p className="text-gray-400 mb-6">No security issues found in your scan.</p>
                        
                        {/* Debug Information Panel - only show if actually 0 findings */}
                        <details className="mt-6 p-4 bg-gray-800 rounded-lg text-left max-w-2xl mx-auto">
                          <summary className="text-sm font-semibold text-gray-300 cursor-pointer mb-2">
                            🐛 Debug Information
                          </summary>
                          <div className="text-xs text-gray-400 space-y-2 mt-2">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <div className="font-medium text-gray-300">Scan Details:</div>
                                <div>ID: {scanId || 'null'}</div>
                                <div>Has Scan Data: {scanData ? '✅' : '❌'}</div>
                                <div>Status: {scanData?.status || 'unknown'}</div>
                                <div>Source: {scanData?.source_type || 'unknown'}</div>
                              </div>
                              <div>
                                <div className="font-medium text-gray-300">Findings:</div>
                                <div>Raw Count: {scanData?.findings?.length || 0}</div>
                                <div>Has Grouped: {groupedFindings ? '✅' : '❌'}</div>
                                <div>Total Calculated: {totalFindings}</div>
                              </div>
                            </div>
                            
                            {groupedFindings && (
                              <div className="mt-3 p-2 bg-gray-900 rounded">
                                <div className="font-medium text-gray-300 mb-1">Category Breakdown:</div>
                                <div>Security: {groupedFindings.security?.length || 0}</div>
                                <div>Privacy: {groupedFindings.privacy?.length || 0}</div>
                                <div>Compliance: {groupedFindings.compliance?.length || 0}</div>
                                <div>Miscellaneous: {groupedFindings.miscellaneous?.length || 0}</div>
                              </div>
                            )}
                            
                            {scanData?.findings && scanData.findings.length > 0 && (
                              <div className="mt-3 p-2 bg-gray-900 rounded">
                                <div className="font-medium text-gray-300 mb-1">Sample Finding:</div>
                                <pre className="text-xs overflow-auto">
                                  {JSON.stringify(scanData.findings[0], null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* AI Fix Assistant - Right Column - ARIA landmark */}
        {isAIAssistantOpen && (
          <aside 
            aria-label="AI Fix Assistant" 
            className="flex-shrink-0 transition-all duration-300 ease-in-out"
            style={{ width: '400px' }}
          >
            <AIFixAssistant
              isOpen={isAIAssistantOpen}
              onClose={handleAIAssistantClose}
              scanId={scanId || ''}
              findings={allFindings}
              totalFindings={allFindings.length}
            />
          </aside>
        )}
      </main>
      
      {/* Mobile Code Drawer - Only show on mobile when file is selected */}
      {selectedFile && (
        <div className="lg:hidden">
          <CodeDrawer
            fileName={selectedFile}
            findings={selectedFileFindings}
            isProUser={isProUser}
            onClose={handleCodePanelClose}
            isOpen={true}
            className="z-50"
            activeFinding={activeFinding || undefined}
          />
        </div>
      )}
      
      {/* Feedback FAB - Only show for authenticated users */}
      <FeedbackFAB onClick={() => openModal()} />
      
      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isOpen}
        onClose={closeModal}
        initialPage={initialPage}
      />
      
      <Footer />
    </div>
  );
} 